import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { PawPrint, LayoutDashboard, Plane, AlertTriangle, Settings, LogOut, Menu, X, Crown, ShieldCheck } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navigation() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isPremium = user?.plan === "premium";

  const navLinks = [
    { href: "/dashboard", label: t("nav.dashboard"), testLabel: "dashboard", icon: LayoutDashboard },
    { href: "/travel", label: t("nav.travel"), testLabel: "travel-mode", icon: Plane, premium: true },
    { href: "/emergency", label: t("nav.emergency"), testLabel: "emergency", icon: AlertTriangle },
  ];

  const isActive = (href) => location.pathname === href;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm font-['Figtree']">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 rounded-full bg-[#FF8A65] flex items-center justify-center">
            <PawPrint size={17} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="font-['Fredoka_One'] text-xl text-slate-800 tracking-wide">Pet Atlas</span>
          {isPremium && (
            <span className="hidden sm:flex items-center gap-0.5 text-xs font-bold text-[#FF8A65] bg-[#FFF5E6] px-2 py-0.5 rounded-full">
              <Crown size={10} /> {t("nav.premium")}
            </span>
          )}
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(({ href, label, testLabel, icon: Icon, premium }) => (
            <button
              key={href}
              data-testid={`nav-${testLabel}`}
              onClick={() => navigate(href)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive(href)
                  ? "bg-[#FF8A65] text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
              }`}
            >
              <Icon size={15} strokeWidth={2.5} />
              {label}
              {premium && !isPremium && (
                <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold">{t("nav.pro")}</span>
              )}
            </button>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Language picker (desktop) */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          {/* User menu */}
          <div className="relative hidden md:block">
            <button
              data-testid="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition-colors"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border-2 border-[#FF8A65]/30" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#FF8A65] flex items-center justify-center text-white font-bold text-sm">
                  {user?.name?.[0] || "U"}
                </div>
              )}
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-12 w-52 bg-white rounded-2xl shadow-lg border border-slate-100 py-2 z-50 animate-fade-in">
                <div className="px-4 py-2 border-b border-slate-100 mb-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <button onClick={() => { navigate("/settings"); setShowUserMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Settings size={15} /> {t("nav.settings")}
                </button>
                <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  <LogOut size={15} /> {t("nav.signOut")}
                </button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            {mobileOpen ? <X size={20} className="text-slate-600" /> : <Menu size={20} className="text-slate-600" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-1 animate-fade-in">
          {navLinks.map(({ href, label, icon: Icon }) => (
            <button
              key={href}
              onClick={() => { navigate(href); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive(href) ? "bg-[#FFF5E6] text-[#FF8A65]" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Icon size={17} strokeWidth={2.5} /> {label}
            </button>
          ))}
          <div className="border-t border-slate-100 pt-2 mt-2">
            <button onClick={() => { navigate("/settings"); setMobileOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-slate-50">
              <Settings size={17} /> {t("nav.settings")}
            </button>
            {user?.is_admin && (
              <button
                data-testid="admin-link-mobile"
                onClick={() => { navigate("/admin"); setMobileOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#FF8A65] hover:bg-[#FFF5E6] font-semibold"
              >
                <ShieldCheck size={17} /> {t("nav.admin")}
              </button>
            )}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-red-600 hover:bg-red-50">
              <LogOut size={17} /> {t("nav.signOut")}
            </button>
          </div>
          {/* Language picker (mobile) */}
          <div className="border-t border-slate-100 pt-2 mt-2">
            <LanguageSwitcher variant="inline" />
          </div>
        </div>
      )}
    </nav>
  );
}
